/**
 * LeadDetail Info-tab helpers
 * ----------------------------
 * Extracted from CoachDashboard.jsx (2026-06-08) as part of the P3
 * leadDetail/{Info,Billing,Schedule} split. JSX is copied VERBATIM —
 * no styling or behavioral changes. Each panel is a self-contained
 * component already used inline inside LeadDetail.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ClipboardList, Mail, FileSignature, ChevronDown, CheckCircle2, Plus, Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// _daysSinceIso copy — same logic as the one in CoachDashboard.jsx (used by
// LeadReplyControls to render "Xd ago" stalewarnings without round-tripping).
function _daysSinceIso(ts) {
    if (!ts) return null;
    try {
        const ms = Date.now() - new Date(ts).getTime();
        return Math.floor(ms / 86400000);
    } catch {
        return null;
    }
}


/**
 * LeadReplyControls — surfaces stale-lead status + manual "Mark replied"
 * toggle. Renders a card on Lead Detail:
 *   • Red "⚠ Stale — no reply in Xd" call-out when stale_lead=true
 *   • Green "✓ Marked replied" confirmation when parent_replied_at set
 *   • Neutral grey hint while inside the 48h window
 *
 * The button is a single click — flips the state via the backend toggle
 * endpoints and re-fetches the lead to keep status in sync.
 */
export function LeadReplyControls({ lead, onChanged }) {
    const [saving, setSaving] = useState(false);
    const sentAt = lead.assessment_email_sent_at;
    const replied = !!lead.parent_replied_at;
    const stale = !!lead.stale_lead;

    const daysSinceSent = sentAt ? _daysSinceIso(sentAt) : null;
    const repliedDays = replied ? _daysSinceIso(lead.parent_replied_at) : null;
    const repliedLabel = repliedDays === null
        ? null
        : repliedDays === 0
            ? "today"
            : repliedDays === 1
                ? "1d ago"
                : `${repliedDays}d ago`;

    if (!sentAt && !replied) {
        // No assessment email yet — nothing useful to show.
        return null;
    }

    const handleMark = async (mark) => {
        setSaving(true);
        try {
            const endpoint = mark ? "mark-replied" : "unmark-replied";
            await axios.post(
                `${API}/coach/leads/${lead.user_id}/${endpoint}`,
                {},
                { withCredentials: true }
            );
            toast.success(mark ? "Marked replied" : "Reply mark cleared");
            await onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update reply status");
        } finally {
            setSaving(false);
        }
    };

    const tone = replied
        ? "border-emerald-700/70 bg-emerald-950/30 text-emerald-200"
        : stale
            ? "border-rose-700/70 bg-rose-950/30 text-rose-200"
            : "border-zinc-800 bg-zinc-950 text-zinc-300";

    return (
        <div
            data-testid="lead-reply-controls"
            className={`px-6 py-4 border-b border-zinc-900 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${tone}`}
        >
            <div className="flex items-start gap-2 text-sm">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                    {replied ? (
                        <div>
                            <div className="font-semibold tracking-wide">
                                ✓ Replied {repliedLabel}
                            </div>
                            <div className="text-xs text-emerald-300/80 mt-0.5">
                                Marked by {lead.parent_replied_by_name || "—"}
                            </div>
                        </div>
                    ) : stale ? (
                        <div>
                            <div className="font-semibold tracking-wide">
                                ⚠ Stale — no reply in {daysSinceSent}d
                            </div>
                            <div className="text-xs text-rose-300/80 mt-0.5">
                                Assessment email sent {daysSinceSent}d ago. Follow up before they go cold.
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="font-semibold tracking-wide text-zinc-200">
                                Awaiting reply
                            </div>
                            <div className="text-xs text-zinc-400 mt-0.5">
                                Assessment email sent {daysSinceSent === 0 ? "today" : `${daysSinceSent}d ago`}. We&apos;ll flag this red after 48h.
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {replied ? (
                    <button
                        type="button"
                        data-testid="lead-unmark-replied"
                        disabled={saving}
                        onClick={() => handleMark(false)}
                        className="text-[10px] tracking-[0.2em] uppercase border border-zinc-700 text-zinc-300 hover:text-white hover:border-white px-3 py-2 transition-colors disabled:opacity-50"
                    >
                        Clear reply mark
                    </button>
                ) : (
                    <button
                        type="button"
                        data-testid="lead-mark-replied"
                        disabled={saving}
                        onClick={() => handleMark(true)}
                        className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-2 transition-colors disabled:opacity-50 ${
                            stale
                                ? "border-rose-500 bg-rose-500 text-white hover:bg-rose-600 hover:border-rose-600"
                                : "border-emerald-600 text-emerald-300 hover:bg-emerald-600 hover:text-white"
                        }`}
                    >
                        {saving ? "Saving…" : "✓ Mark replied"}
                    </button>
                )}
            </div>
        </div>
    );
}


/**
 * IntakeSubmissionPanel — collapsible section showing exactly what the
 * client filled out when they booked their assessment. Pre-collapsed
 * for cleanliness; one click expands to reveal goals, preferred time,
 * notes, "how did you find us", waiver intent, anti-bot flags, etc.
 *
 * If multiple submissions exist (re-booking), shows them stacked newest
 * first with a small "Resubmitted" caption.
 */
export function IntakeSubmissionPanel({ submissions }) {
    const [open, setOpen] = useState(false);
    if (!submissions || submissions.length === 0) return null;

    const REFERRAL_LABELS = {
        google: "Google search",
        instagram: "Instagram",
        facebook: "Facebook",
        referral: "Referred by someone",
        drive_by: "Drove by / saw the gym",
        event: "Event or camp",
        other: "Other",
    };

    return (
        <div className="p-6 border-b border-zinc-900" data-testid="intake-submission-panel">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2 text-left"
                data-testid="intake-submission-toggle"
            >
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 flex items-center gap-2">
                    <ClipboardList className="w-3 h-3" />
                    Intake submission
                    <span className="text-zinc-500 normal-case tracking-normal">
                        ({submissions.length} {submissions.length === 1 ? "answer" : "answers"})
                    </span>
                </div>
                <ChevronDown
                    className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {!open && (
                <p className="text-[11px] text-zinc-500 mt-2">
                    What they filled out when they booked — click to expand.
                </p>
            )}

            {open && (
                <div className="mt-3 space-y-3" data-testid="intake-submission-content">
                    {submissions.map((s, idx) => (
                        <div
                            key={s.request_id || idx}
                            data-testid={`intake-submission-row-${idx}`}
                            className="border border-zinc-800 rounded-lg bg-zinc-950/40 p-4"
                        >
                            {idx > 0 && (
                                <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300 mb-2">
                                    Re-submitted · {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                                </div>
                            )}
                            {idx === 0 && s.created_at && (
                                <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
                                    Submitted {new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                    {s.status && s.status !== "pending" && (
                                        <span className="ml-2 text-emerald-400">· {s.status}</span>
                                    )}
                                </div>
                            )}
                            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                                <IntakeField label="Signing up for" value={
                                    s.signup_for === "self" ? "Themselves (adult)"
                                    : s.signup_for === "both" ? "Self + kids"
                                    : "Their kid(s)"
                                } />
                                <IntakeField label="Preferred time" value={s.preferred_time} />
                                <IntakeField
                                    label="How they found us"
                                    value={
                                        REFERRAL_LABELS[s.referral_source] || s.referral_source || "—"
                                    }
                                    extra={s.referral_details}
                                />
                                <IntakeField
                                    label="Sign waiver right away?"
                                    value={s.sign_waiver_now ? "Yes — wants to sign now" : "Will sign at the gym"}
                                />
                            </dl>
                            {s.goals && (
                                <div className="mt-3 pt-3 border-t border-zinc-900">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">Goals · &ldquo;Why are you here?&rdquo;</div>
                                    <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{s.goals}</p>
                                </div>
                            )}
                            {s.notes && (
                                <div className="mt-3 pt-3 border-t border-zinc-900">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">Notes</div>
                                    <p className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{s.notes}</p>
                                </div>
                            )}
                            {s.suspicious_signals?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-amber-900/40">
                                    <div className="text-[10px] tracking-[0.2em] uppercase text-amber-300 mb-1">⚠ Anti-bot flags</div>
                                    <p className="text-[11px] text-amber-200">{s.suspicious_signals.join(" · ")}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}


export function IntakeField({ label, value, extra }) {
    return (
        <div>
            <dt className="text-[10px] tracking-[0.2em] uppercase text-zinc-400">{label}</dt>
            <dd className="text-sm text-white mt-0.5">
                {value || <span className="text-zinc-600">—</span>}
                {extra && (
                    <span className="block text-[11px] text-zinc-400 mt-0.5 italic">&ldquo;{extra}&rdquo;</span>
                )}
            </dd>
        </div>
    );
}


/**
 * CommunicationLogPanel — every email send attempt scoped to this lead.
 *
 * Solves the "I clicked a button but no email arrived — was it sent?" UX
 * gap. Pulls /coach/leads/{user_id}/email-log on mount and renders the most
 * recent 20 attempts with kind / subject / status / time + a per-row
 * "Re-send" button (owner-only — POSTs back to the resend endpoint).
 *
 * If the log is empty → renders an explicit "No emails sent to this lead
 * yet" message so the coach knows the absence is real, not a bug.
 */
export function CommunicationLogPanel({ userId, leadEmail }) {
    const { user: me } = useAuth();
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [resending, setResending] = useState(null);
    const [composeOpen, setComposeOpen] = useState(false);
    // Per-coach permission: email_clients gates the whole communication
    // panel (backend 403s the log + send endpoints without it).
    const canEmail = me?.role === "owner" || !!me?.permissions?.email_clients;

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(
                `${API}/coach/leads/${userId}/email-log`,
                { withCredentials: true },
            );
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load email log", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    // Lazy load when the section is expanded for the first time.
    useEffect(() => { if (open && data === null) load(); }, [open]); // eslint-disable-line

    const resend = async (logId) => {
        setResending(logId);
        try {
            const r = await axios.post(
                `${API}/coach/leads/${userId}/email-log/${logId}/resend`,
                {},
                { withCredentials: true },
            );
            toast.success(r.data?.message || "Email re-sent");
            await load();
        } catch (e) {
            toast.error("Resend failed", { description: e?.response?.data?.detail });
        } finally {
            setResending(null);
        }
    };

    const log = data?.log || [];
    if (!canEmail) return null;
    return (
        <div className="p-6 border-b border-zinc-900" data-testid="communication-log-panel">
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="flex-1 flex items-center justify-between gap-2 text-left"
                    data-testid="communication-log-toggle"
                >
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 flex items-center gap-2">
                        <Mail className="w-3 h-3" />
                        Communication log
                        {data && (
                            <span className="text-zinc-500 normal-case tracking-normal">
                                ({log.length} send{log.length === 1 ? "" : "s"})
                            </span>
                        )}
                    </div>
                    <ChevronDown
                        className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
                    />
                </button>
                <button
                    type="button"
                    data-testid="communication-log-new-message"
                    onClick={() => setComposeOpen(true)}
                    className="shrink-0 inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-zinc-800 text-zinc-200 hover:border-brand-red hover:text-brand-red transition-colors"
                    title="Compose a new email to this client"
                >
                    <Plus className="w-3 h-3" /> New message
                </button>
            </div>

            {!open && (
                <p className="text-[11px] text-zinc-500 mt-2">
                    Every email we tried to send to this lead — verify it sent and re-fire if needed.
                </p>
            )}

            {open && (
                <div className="mt-3" data-testid="communication-log-content">
                    {busy && !data ? (
                        <div className="text-zinc-500 text-xs py-4">Loading…</div>
                    ) : log.length === 0 ? (
                        <p className="text-xs text-zinc-500 py-3">
                            No emails sent to this lead yet. If a flow should have sent an email
                            and you don&apos;t see it here, the email <strong>never even fired</strong> — check the
                            Network tab in Safari Developer Tools.
                        </p>
                    ) : (
                        <div className="border border-zinc-800 rounded-lg bg-zinc-950/40 divide-y divide-zinc-900 overflow-hidden">
                            {log.map((row) => {
                                const statusMeta = {
                                    delivered: { label: "Delivered", color: "text-emerald-400" },
                                    queued: { label: "Queued", color: "text-zinc-300" },
                                    failed: { label: "Failed", color: "text-rose-400" },
                                }[row.status] || { label: row.status || "Unknown", color: "text-zinc-400" };
                                return (
                                    <div key={row.log_id} data-testid={`email-log-row-${row.log_id}`} className="px-4 py-3 flex items-start justify-between gap-3">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-white truncate">{row.subject || "(no subject)"}</div>
                                            <div className="text-[10px] tracking-wider uppercase text-zinc-500 mt-1 flex flex-wrap items-center gap-x-3">
                                                <span className={statusMeta.color}>{statusMeta.label}</span>
                                                <span>· {row.kind || "other"}</span>
                                                <span>· {row.sent_at ? new Date(row.sent_at).toLocaleString() : "—"}</span>
                                                {(row.to || []).length > 0 && <span>· to {(row.to || [])[0]}</span>}
                                            </div>
                                            {row.error && (
                                                <p className="text-[11px] text-rose-300 mt-1 break-words">
                                                    {row.error}
                                                </p>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => resend(row.log_id)}
                                            disabled={resending === row.log_id}
                                            data-testid={`email-log-resend-${row.log_id}`}
                                            className="shrink-0 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-zinc-800 text-zinc-200 hover:border-brand-red hover:text-brand-red transition-colors disabled:opacity-40"
                                            title="Re-send this exact email"
                                        >
                                            {resending === row.log_id ? "…" : "Re-send"}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {composeOpen && (
                <ComposeMessageModal
                    userId={userId}
                    leadEmail={leadEmail || data?.lead?.email}
                    onClose={() => setComposeOpen(false)}
                    onSent={() => {
                        setComposeOpen(false);
                        setOpen(true);
                        load();
                    }}
                />
            )}
        </div>
    );
}


/**
 * ComposeMessageModal — "New message" composer for the Communication Log.
 * Plain-text body; the backend wraps it in the MaxSC-branded email shell
 * with the sending coach's signoff, logs it to email_log, and stamps an
 * outreach touch (last_contacted + contact_log).
 */
function ComposeMessageModal({ userId, leadEmail, onClose, onSent }) {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const noEmail = !leadEmail || leadEmail.endsWith("@maxsc.local");
    const canSend = !noEmail && subject.trim().length > 0 && body.trim().length > 0 && !sending;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSend) return;
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/coach/leads/${userId}/email-log/send`,
                { subject: subject.trim(), body: body.trim() },
                { withCredentials: true },
            );
            toast.success("Message sent", { description: `Delivered to ${r.data?.to}.` });
            onSent();
        } catch (err) {
            toast.error("Couldn't send", { description: err?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
            data-testid="compose-message-modal"
            onClick={onClose}
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl p-7 w-full max-w-xl shadow-2xl my-8"
            >
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5" /> New message
                </div>
                <h2 className="font-display text-2xl tracking-wider text-white mb-4">Email this client</h2>

                <div className="space-y-4">
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">To</label>
                        {noEmail ? (
                            <div data-testid="compose-no-email" className="text-amber-300 text-sm border border-amber-500/40 bg-amber-950/20 rounded px-3 py-2">
                                No email on this profile — add one first from the profile header.
                            </div>
                        ) : (
                            <div data-testid="compose-to" className="text-white text-sm bg-zinc-900 border border-zinc-800 rounded px-3 py-2">
                                {leadEmail}
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Subject *</label>
                        <input
                            type="text"
                            data-testid="compose-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            maxLength={200}
                            autoFocus
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-base px-3 py-2 rounded focus:outline-none focus:border-brand-red"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Message *</label>
                        <textarea
                            data-testid="compose-body"
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            rows={7}
                            maxLength={10000}
                            placeholder="Write your message — it'll go out in the MaxSC email design, signed by you. Replies come back to the gym inbox."
                            className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm leading-relaxed px-3 py-2 rounded focus:outline-none focus:border-brand-red resize-y"
                        />
                    </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="compose-cancel"
                        className="text-zinc-400 hover:text-white text-xs tracking-wider uppercase px-3 py-2"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        data-testid="compose-send"
                        disabled={!canSend}
                        className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                        {sending ? "Sending…" : "Send message"}
                    </button>
                </div>
            </form>
        </div>
    );
}


/**
 * WaiverPanel — coach-side view of the parent's signed waiver.
 *
 * Three states:
 *   1. Waiver not in lead payload → owner doesn't have view_waiver perm OR
 *      no waiver exists yet. Show muted "No waiver on file" caption.
 *   2. Waiver present → green check, signed name + signed-at date + IP +
 *      source + a "View full text" expander showing the frozen waiver
 *      snapshot they actually agreed to.
 */
export function WaiverPanel({ waiver, userEmail, userId }) {
    const [showText, setShowText] = useState(false);
    const [resendBusy, setResendBusy] = useState(false);
    const { user: me } = useAuth();
    const isOwner = me?.role === "owner";
    if (waiver === undefined) {
        // Coach without view_waiver perm — backend strips this field entirely.
        return null;
    }

    const onResend = async (force = false) => {
        if (!userId) {
            toast.error("Can't resend yet — lead has no user account.");
            return;
        }
        setResendBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/waivers/resend`,
                { user_id: userId, force },
                { withCredentials: true },
            );
            if (r.data?.sent) {
                toast.success(`Waiver email sent to ${r.data.to}`);
            } else if (r.data?.skipped_reason === "throttled") {
                toast.warning(r.data.message || "Throttled — wait 1 hour");
            } else if (r.data?.skipped_reason === "already_signed") {
                toast.info(r.data.message || "Already signed");
            } else {
                toast.info(r.data?.message || "Skipped");
            }
        } catch (e) {
            toast.error("Couldn't send waiver email", { description: e?.response?.data?.detail });
        } finally {
            setResendBusy(false);
        }
    };

    if (!waiver) {
        return (
            <div className="p-6 border-b border-zinc-900" data-testid="lead-waiver-panel">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 inline-flex items-center gap-2">
                            <FileSignature className="w-3 h-3" />
                            Waiver
                            <span className="text-amber-300 normal-case tracking-normal">· Not signed</span>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-2 max-w-md">
                            No waiver on file. The booking email already included a signing link; use the button to resend it (rate-limited to once per hour).
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            data-testid="resend-waiver-btn"
                            onClick={() => onResend(false)}
                            disabled={resendBusy || !userId}
                            title={!userId ? "Lead has no user account yet — pre-account waivers are sent automatically with the booking email." : "Resend the waiver signing link to this person."}
                            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-brand-red bg-brand-red/10 text-brand-red hover:bg-brand-red hover:text-white transition-colors disabled:opacity-50"
                        >
                            {resendBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                            Resend waiver
                        </button>
                        {isOwner && (
                            <button
                                type="button"
                                data-testid="resend-waiver-force-btn"
                                onClick={() => onResend(true)}
                                disabled={resendBusy || !userId}
                                title="Owner override — bypass the 1-hour throttle and send immediately."
                                className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase px-2 py-2 border border-zinc-700 text-zinc-300 hover:text-amber-300 hover:border-amber-500 transition-colors disabled:opacity-50"
                            >
                                Force
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    const dt = waiver.signed_at ? new Date(waiver.signed_at) : null;
    return (
        <div className="p-6 border-b border-zinc-900" data-testid="lead-waiver-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 inline-flex items-center gap-2">
                        <FileSignature className="w-3 h-3" />
                        Waiver
                        <span className="text-emerald-300 normal-case tracking-normal inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Signed
                        </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                        <div>
                            <dt className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Signed by</dt>
                            <dd className="text-white">{waiver.signed_name || "—"}</dd>
                        </div>
                        <div>
                            <dt className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Date</dt>
                            <dd className="text-white">
                                {dt ? dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"}
                                {dt && <span className="text-zinc-500"> · {dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Relationship</dt>
                            <dd className="text-white">{waiver.relationship || "self"}</dd>
                        </div>
                        <div>
                            <dt className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Version</dt>
                            <dd className="text-white">{waiver.version || "—"}</dd>
                        </div>
                        {waiver.signed_ip && (
                            <div>
                                <dt className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Signed IP</dt>
                                <dd className="text-zinc-300 font-mono text-[11px]">{waiver.signed_ip}</dd>
                            </div>
                        )}
                        {userEmail && (
                            <div>
                                <dt className="text-[10px] tracking-[0.18em] uppercase text-zinc-500">Email on record</dt>
                                <dd className="text-zinc-300 text-[11px]">{userEmail}</dd>
                            </div>
                        )}
                    </dl>
                </div>
                {waiver.text_snapshot && (
                    <button
                        type="button"
                        data-testid="lead-waiver-view-text"
                        onClick={() => setShowText((v) => !v)}
                        className="text-[10px] tracking-[0.15em] uppercase px-2.5 py-1.5 border border-zinc-800 text-zinc-200 hover:border-brand-red hover:text-brand-red transition-colors shrink-0"
                    >
                        {showText ? "Hide text" : "View text"}
                    </button>
                )}
            </div>
            {showText && waiver.text_snapshot && (
                <div data-testid="lead-waiver-text-snapshot" className="mt-4 border border-zinc-800 rounded bg-zinc-950 p-4 max-h-96 overflow-y-auto">
                    <p className="text-[11px] tracking-wider uppercase text-zinc-500 mb-2">Frozen text they agreed to</p>
                    <pre className="text-[12px] text-zinc-300 whitespace-pre-wrap font-sans leading-relaxed">{waiver.text_snapshot}</pre>
                </div>
            )}
        </div>
    );
}
